import { createResourceContentSelector } from 'util/selector/resourceSelector';

import { createSelector } from '@reduxjs/toolkit';
import groupBy from 'lodash/groupBy';
import { LogicDependencyModel } from 'api/dto/Logic.dto';
import { PageModel } from 'api/dto/Page.dto';
import { PageItemKind } from 'model/PageItemKind';
import { Question } from 'model/Question';
import { QuestionOpinion } from 'model/QuestionOpinion';
import { AdminState } from 'reduxStore/appStore';
import { selectModalParam } from 'reduxStore/modal/selectors';
import { PageState } from 'reduxStore/page/initialState';
import { AdminModal } from 'register/AdminModal';
import { LogicType } from 'model/LogicType';

export const selectPageDomain = (state: AdminState) => state.page as PageState.Domain;

export const selectPageListResource = createSelector(
  selectPageDomain,
  (domain) => domain.listResource,
);

export const selectPageList = createResourceContentSelector(selectPageListResource);

export const selectCurrentItem = createSelector(selectPageDomain, (domain) => domain.currentItem);

export const selectCurrentItemByLanguage = createSelector(
  selectPageDomain,
  (domain) => domain.currentItemByLanguage,
);

export const selectLanguageTab = createSelector(selectPageDomain, (domain) => domain.languageTab);

export const selectQuestionDetailsTab = createSelector(
  selectPageDomain,
  (domain) => domain.questionDetailsTab,
);

export const selectSelectedPage = createSelector(
  selectModalParam(AdminModal.TextOrMedia, 'page'),
  selectModalParam(AdminModal.TextOrMediaMultiLanguage, 'page'),
  selectModalParam(AdminModal.Question, 'page'),
  selectModalParam(AdminModal.QuestionMultiLanguage, 'page'),
  (...pages: PageModel[]) => pages.find((page) => !!page),
);

export const selectQuestionPosition = createSelector(
  selectPageListResource,
  selectSelectedPage,
  (listResource, selectedPage) =>
    listResource.loadingState === 'resolved' && listResource.data
      ? listResource.data
          .filter((page) => page.id <= (selectedPage && selectedPage.id))
          .map((page) => page.items.length)
          .reduce((sum, count) => sum + count + 1, 0)
      : null,
);

export const selectItemKind = createSelector(selectCurrentItem, (currentItem) =>
  currentItem ? currentItem.kind : null,
);

export const selectPreviousPages = createSelector(
  selectPageList,
  selectSelectedPage,
  (pages, selectedPage) =>
    pages
      ? pages.filter((page) => page && page.position <= (selectedPage && selectedPage.position))
      : null,
);

export const selectPreviousDiscreteQuestionsByPage = createSelector(
  selectPreviousPages,
  selectSelectedPage,
  selectCurrentItem,
  selectQuestionPosition,
  (pages, selectedPage, currentItem, currentPosition) =>
    pages.map((page) => {
      if (page.position < selectedPage.position) {
        return PageModel.getDiscreteQuestions(page);
      } else if (page.position === selectedPage.position) {
        return PageModel.getDiscreteQuestions(page)
          .map((question) =>
            currentItem
              ? question.position < currentItem.position
                ? question
                : null
              : question.position < currentPosition
                ? question
                : null,
          )
          .filter((question) => question !== null);
      } else {
        return null;
      }
    }),
);

export const selectQuestions = createSelector(selectPageList, (pages) =>
  pages
    ? pages.reduce((questions, page) => questions.concat(PageModel.getQuestions(page)), [])
    : [],
);

export const selectOnlyTemplateQuestions = createSelector(selectPageList, (pages) =>
  pages
    ? pages.reduce((questions, page) => {
        /**
         * 1 is hardcoded position for template questions
         */
        if (page.position !== 1) {
          return questions;
        }

        return questions.concat(PageModel.getQuestions(page));
      }, [])
    : [],
);

export const selectMediaQuestions = createSelector(selectPageList, (pages) => {
  return pages
    ? pages.reduce((questions, page) => questions.concat(PageModel.getMediaQuestions(page)), [])
    : [];
});

export const selectAllDependencyLogicIds = createSelector(
  selectQuestions,
  (questions) =>
    questions &&
    questions
      .filter(
        (question: Question) => !!question.logic && question.logic?.type === LogicType.Dependency,
      )
      .map((question: Question) => question.logic)
      .map((logic: LogicDependencyModel) => logic.options)
      .reduce((acc, e) => acc.concat(e), []),
);

export const selectHasClassificationOrDependencyLogic = createSelector(
  selectQuestions,
  selectMediaQuestions,
  (questions, media) =>
    (questions || media) &&
    questions
      .concat(media)
      .some(
        (question: Question) =>
          !!question.logic &&
          (question.logic.type === LogicType.Classification ||
            question.logic.type === LogicType.Distribution),
      ),
);

export const selectHasClassificationLogic = createSelector(
  selectQuestions,
  selectMediaQuestions,
  (questions, media) =>
    (questions || media) &&
    questions
      .concat(media)
      .some(
        (question: Question) =>
          !!question.logic && question.logic.type === LogicType.Classification,
      ),
);

export const selectHasDistributionLogic = createSelector(
  selectQuestions,
  selectMediaQuestions,
  (questions, media) =>
    (questions || media) &&
    questions
      .concat(media)
      .some(
        (question: Question) => !!question.logic && question.logic.type === LogicType.Distribution,
      ),
);

export const selectHasLoadedPages = createSelector(selectPageList, (pages) => !!pages);

export const selectGroupedQuestionList = createSelector(
  selectOnlyTemplateQuestions,
  (questions: (Question | QuestionOpinion)[]) => {
    const groupByCallback = (question: Question | QuestionOpinion) => {
      if (question.kind === PageItemKind.QuestionOpinion) {
        // TODO page state QUESTIONS for main and sub project must be splitted because of issue with clearing state
        return (question as QuestionOpinion).category?.name;
      }

      return 'Open Questions';
    };

    return (
      questions &&
      groupBy(
        questions.filter((question) =>
          [PageItemKind.QuestionOpinion, PageItemKind.QuestionCommentsBox].includes(question.kind),
        ),
        groupByCallback,
      )
    );
  },
);
